import * as PortOne from "@portone/browser-sdk/v2";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { identityVerificationService } from "../services/identityVerificationService";
import { STORAGE_KEYS } from "../types/storage";
import { useAuth } from "./useAuth";

export interface UseIdentityVerificationReturn {
  isLoading: boolean;
  error: string | null;
  startVerification: (phoneNumber: string) => Promise<void>;
}

/**
 * 고유한 본인인증 ID 생성
 */
const generateVerificationId = (): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `identity-${timestamp}-${random}`;
};

export const useIdentityVerification = (): UseIdentityVerificationReturn => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startVerification = useCallback(async (phoneNumber: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. 백엔드에서 storeId 조회
      const { storeId } = await identityVerificationService.getConfig();

      // 2. 프론트엔드에서 identityVerificationId 생성
      const verificationId = generateVerificationId();

      // 3. 포트원 SDK로 본인인증 요청
      // customer.phoneNumber를 전달하여 본인인증 창에서 전화번호 고정
      const channelKey = import.meta.env.VITE_PORTONE_CHANNEL_KEY || "homedoc";

      // 모바일에서 리다이렉션 방식 사용 시 필요한 redirectUrl 설정
      const redirectUrl = `${window.location.origin}/auth/identity-verification/callback`;

      // 리다이렉션 방식에서 콜백 후 사용할 데이터를 sessionStorage에 저장
      sessionStorage.setItem(
        STORAGE_KEYS.PHONE_NUMBER,
        phoneNumber.replace(/-/g, "")
      );
      sessionStorage.setItem(STORAGE_KEYS.VERIFICATION_ID, verificationId);

      const response = await PortOne.requestIdentityVerification({
        storeId,
        identityVerificationId: verificationId,
        channelKey,
        redirectUrl,
        customer: {
          phoneNumber: phoneNumber.replace(/-/g, ""), // 하이픈 제거
        },
      });

      if (response?.code) {
        // 에러 발생
        console.error("[PortOne] 본인인증 실패:", {
          code: response.code,
          message: response.message,
          usedChannelKey: channelKey,
        });
        setError(response.message || "본인인증에 실패했습니다.");
        return;
      }

      // 4. 백엔드에서 본인인증 결과 확인 (프론트에서 입력받은 전화번호 전달)
      const confirmResult =
        await identityVerificationService.confirmVerification({
          identityVerificationId: verificationId,
          phoneNumber: phoneNumber.replace(/-/g, ""),
        });

      // 데스크탑 팝업 방식 성공 시 sessionStorage 정리
      sessionStorage.removeItem(STORAGE_KEYS.PHONE_NUMBER);
      
      if (confirmResult.userExists) {
        // 기존 사용자인 경우 identityVerificationId 제거
        sessionStorage.removeItem(STORAGE_KEYS.VERIFICATION_ID);
        // 기존 사용자 - 로그인 처리
        if (confirmResult.accessToken && confirmResult.refreshToken) {
          login(confirmResult.accessToken, confirmResult.refreshToken);
          navigate("/", { replace: true });
        }
      } else {
        if (!confirmResult.verificationData) {
          throw new Error("본인인증 결과 데이터가 없습니다.");
        }
        // 신규 사용자 - 약관동의 페이지로 이동
        navigate("/auth/terms");
      }
    } catch (err) {
      console.error("본인인증 실패:", err);
      setError(
        err instanceof Error
          ? err.message
          : "본인인증 처리 중 오류가 발생했습니다."
      );
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate]);

  return {
    isLoading,
    error,
    startVerification,
  };
};
